import { Notification } from "@/app/core/apis/notification.service";

const UUID_V4ISH_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizePathname(pathname: string): string {
  return pathname.replace(/^\/+/, "/").replace(/\/{2,}/g, "/");
}

function decisionLabel(decision: unknown): string {
  if (decision === "approved") return "approved";
  if (decision === "rejected" || decision === "declined") return "declined";
  return "updated";
}

export function getNotificationListTitle(n: Notification): string {
  const title = n.metadata?.["title"];
  if (typeof title === "string" && title.trim()) return title.trim();

  switch (n.notificationType) {
    case "payment_request_decision_business":
      return `Payment request ${decisionLabel(n.metadata?.["decision"])}`;
    case "project_invitation_decision_business":
      return `Project invitation ${decisionLabel(n.metadata?.["decision"])}`;
    case "project_revision_decision_business":
      return `Revision request ${decisionLabel(n.metadata?.["decision"])}`;
    case "project_completed_business":
      return "Project completed";
    case "withdrawal_successful_business":
      return "Withdrawal successful";
    case "invoice_payment_business":
      return "Invoice payment received";
    case "dispute_raised_business":
      return "Dispute raised";
    default:
      return "Notification";
  }
}

export function getNotificationListBody(n: Notification): string | null {
  const message = n.metadata?.["message"];
  if (typeof message === "string" && message.trim()) return message.trim();

  const projectName = n.metadata?.["projectName"];
  const clientName = n.metadata?.["clientName"];
  const decision = n.metadata?.["decision"];
  const amountFormatted = n.metadata?.["amountFormatted"];
  const invoiceNumber = n.metadata?.["invoiceNumber"];

  switch (n.notificationType) {
    case "payment_request_decision_business": {
      if (projectName && decision === "approved") {
        return `The client approved a payment request for ${projectName}.`;
      }
      if (projectName && (decision === "rejected" || decision === "declined")) {
        return `The client declined a payment request for ${projectName}.`;
      }
      return projectName ? `Project: ${projectName}` : null;
    }
    case "project_invitation_decision_business": {
      if (clientName && projectName && decision === "approved") {
        return `${clientName} approved your invitation for ${projectName}.`;
      }
      if (clientName && projectName && (decision === "rejected" || decision === "declined")) {
        return `${clientName} declined the invitation for ${projectName}.`;
      }
      if (projectName) return `Project: ${projectName}`;
      return null;
    }
    case "project_revision_decision_business": {
      if (clientName && projectName && decision === "approved") {
        return `${clientName} approved a revision for ${projectName}.`;
      }
      if (clientName && projectName && (decision === "rejected" || decision === "declined")) {
        return `${clientName} declined a revision for ${projectName}.`;
      }
      if (projectName) return `Project: ${projectName}`;
      return null;
    }
    case "project_completed_business": {
      if (clientName && projectName) {
        return `${clientName} marked ${projectName} as complete.`;
      }
      return projectName ? `Project: ${projectName}` : null;
    }
    case "withdrawal_successful_business":
      return "Your withdrawal was successful.";
    case "invoice_payment_business": {
      const parts: string[] = [];
      if (typeof amountFormatted === "string" && amountFormatted.trim()) {
        parts.push(amountFormatted.trim());
      }
      if (typeof invoiceNumber === "string" && invoiceNumber.trim()) {
        parts.push(`Invoice ${invoiceNumber.trim()}`);
      }
      if (typeof projectName === "string" && projectName.trim()) {
        parts.push(projectName.trim());
      }
      return parts.length ? parts.join(" · ") : null;
    }
    default:
      return null;
  }
}

/**
 * Returns an in-app URL (with leading slash) or null when we cannot resolve a destination.
 */
export function getNotificationNavigateUrl(n: Notification, businessId: string): string | null {
  const meta = n.metadata ?? {};

  if (typeof meta["url"] === "string" && meta["url"].trim()) {
    try {
      const u = new URL(meta["url"]);
      const pathname = normalizePathname(u.pathname);

      // /projects/:id[/invoices...] — invoices are a tab on the project workspace, not a route
      const projectsMatch = pathname.match(/^\/projects\/([^/]+)(\/.*)?$/);
      if (projectsMatch?.[1]) {
        const rest = projectsMatch[2] ?? "";
        if (rest.startsWith("/invoices") || n.notificationType === "invoice_payment_business") {
          return `/${businessId}/projects/${projectsMatch[1]}/in_progress?tab=invoices`;
        }
        return `/${businessId}/projects/${projectsMatch[1]}/in_progress`;
      }

      // /:businessId/projects/:id/:status|invoices — preserve workspace status; map invoices → tab
      const scopedProject = pathname.match(/^\/([^/]+)\/projects\/([^/]+)(?:\/([^/]+))?/i);
      if (scopedProject?.[2]) {
        const segment = (scopedProject[3] || "in_progress").toLowerCase();
        const tab = u.searchParams.get("tab");
        const openInvoices =
          segment === "invoices" || tab === "invoices" || n.notificationType === "invoice_payment_business";
        const workspaceStatus =
          segment === "invoices" || segment === "draft" ? "in_progress" : segment;
        if (openInvoices) {
          return `/${businessId}/projects/${scopedProject[2]}/${workspaceStatus}?tab=invoices`;
        }
        if (tab === "conversations") {
          return `/${businessId}/projects/${scopedProject[2]}/${workspaceStatus}?tab=conversations`;
        }
        return `/${businessId}/projects/${scopedProject[2]}/${workspaceStatus}`;
      }

      // /:businessId/wallets|wallet|invoices (business-fe or light-fe absolute URLs)
      const scopedMatch = pathname.match(/^\/([^/]+)\/(wallets?|invoices)(?:\/|$)/i);
      if (scopedMatch?.[1] && scopedMatch?.[2]) {
        const segment = scopedMatch[2].toLowerCase();
        if (segment === "wallet" || segment === "wallets") {
          return `/${businessId}/wallets`;
        }
        if (segment === "invoices") {
          // Prefer project invoices tab when projectId is known
          if (typeof meta["projectId"] === "string" && meta["projectId"]) {
            return `/${businessId}/projects/${meta["projectId"]}/in_progress?tab=invoices`;
          }
          return `/${businessId}/invoices`;
        }
      }

      if (pathname === "/wallets" || pathname.startsWith("/wallets/")) {
        return `/${businessId}/wallets`;
      }
    } catch {
      /* ignore invalid url */
    }
  }

  const projectIdFromMeta = typeof meta["projectId"] === "string" ? meta["projectId"] : undefined;
  const projectIdFromEntity = n.entityType === "project" ? n.entityId : undefined;
  const projectId = projectIdFromMeta ?? projectIdFromEntity;

  if (projectId) {
    if (n.notificationType === "invoice_payment_business") {
      return `/${businessId}/projects/${projectId}/in_progress?tab=invoices`;
    }
    const conversationsTab = n.notificationType === "client_message";
    return conversationsTab
      ? `/${businessId}/projects/${projectId}/in_progress?tab=conversations`
      : `/${businessId}/projects/${projectId}/in_progress`;
  }

  if (n.entityType === "milestone" && typeof meta["projectId"] === "string") {
    return `/${businessId}/projects/${meta["projectId"]}/in_progress`;
  }

  if (n.notificationType === "withdrawal_successful_business") {
    return `/${businessId}/wallets`;
  }

  if (n.notificationType === "invoice_payment_business") {
    return `/${businessId}/invoices`;
  }

  // Last resort: entity is the business itself
  if (n.entityType === "business" && UUID_V4ISH_REGEX.test(n.entityId)) {
    return `/${businessId}/dashboard`;
  }

  return null;
}
